/**
 * NetWorthPage.js — Net Worth Dashboard
 *
 * Shows total net worth hero card, assets vs liabilities breakdown,
 * monthly trend chart, and account groups by type.
 * Data sourced from Firestore accounts + net_worth_snapshots collections.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAccounts, getNetWorthSnapshots, getUserConfig } from '../services/db';
import { calculateNetWorth, groupAccountsByType } from '../utils/networth';
import { formatCurrency, formatDate } from '../utils/format';
import NetWorthChart from '../components/NetWorthChart';
import theme from '../utils/theme';

export default function NetWorthPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    async function load() {
      try {
        const [accts, snaps, config] = await Promise.all([
          getAccounts(user.id),
          getNetWorthSnapshots(user.id, 12),
          getUserConfig(user.id),
        ]);
        setAccounts(accts);
        setSnapshots(snaps);
        const syncTs = config?.monarch_last_sync;
        setLastSync(syncTs?.toDate ? syncTs.toDate() : syncTs instanceof Date ? syncTs : null);
      } catch (err) {
        console.error('Failed to load net worth data:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="page">
        <div className="skeleton-card--dark" style={{ marginBottom: '0.75rem' }}>
          <div className="skeleton-line--hero skeleton-line" />
          <div className="skeleton-line--small skeleton-line" style={{ margin: '0 auto' }} />
        </div>
        <div className="skeleton-card">
          <div className="skeleton-line" />
          <div className="skeleton-line--medium skeleton-line" />
        </div>
        <div className="skeleton-card">
          <div className="skeleton-line" />
          <div className="skeleton-line--medium skeleton-line" />
          <div className="skeleton-line" />
        </div>
      </div>
    );
  }

  const { totalAssets, totalLiabilities, netWorth } = calculateNetWorth(accounts);
  const groups = groupAccountsByType(accounts);

  return (
    <div className="page">
      {/* Hero card */}
      <div className="networth-hero">
        <div className="networth-hero-label">Net Worth</div>
        <div className={`networth-hero-amount ${netWorth < 0 ? 'negative' : ''}`}>
          {formatCurrency(netWorth, false)}
        </div>
        <div className="networth-summary-row">
          <div className="networth-summary-item">
            <div className="networth-summary-label">Assets</div>
            <div className="networth-summary-value assets">
              {formatCurrency(totalAssets, false)}
            </div>
          </div>
          <div className="networth-summary-item">
            <div className="networth-summary-label">Debts</div>
            <div className="networth-summary-value liabilities">
              {formatCurrency(totalLiabilities, false)}
            </div>
          </div>
        </div>
      </div>

      {/* Trend chart */}
      <NetWorthChart snapshots={snapshots} />

      {/* Account groups */}
      {groups.map(group => (
        <div key={group.type} className="networth-group">
          <div className="networth-group-header">
            <span className="networth-group-label">{group.label}</span>
            <span className={`networth-group-total ${group.subtotal < 0 ? 'negative' : ''}`}>
              {formatCurrency(group.subtotal, false)}
            </span>
          </div>
          {group.accounts.map((acct, idx) => (
            <div key={acct.id || idx} className="networth-account-row">
              <div className="networth-account-info">
                <div className="networth-account-name">{String(acct.name || '')}</div>
                {acct.institution && (
                  <div className="networth-account-institution">
                    {typeof acct.institution === 'string' ? acct.institution : acct.institution?.name || ''}
                  </div>
                )}
              </div>
              <div className={`networth-account-balance ${(acct.current_balance || 0) < 0 ? 'negative' : ''}`}>
                {formatCurrency(acct.current_balance || 0)}
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Empty state */}
      {accounts.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem 1rem', color: theme.textMuted }}>
          <p style={{ fontSize: '0.9rem' }}>No accounts synced yet.</p>
          <p style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>Run a sync from Settings to pull your accounts.</p>
        </div>
      )}

      {/* Last synced footer */}
      {lastSync && (
        <div className="networth-sync-footer">
          Last synced {formatDate(lastSync, 'relative')}
        </div>
      )}
    </div>
  );
}
